
import { Link } from "react-router-dom";
import { Slider } from "@/components/ui/slider";
import { useState } from "react";
import { Calculator, Star, Shield, CheckCircle } from "lucide-react";
import { useLoanCalculator } from "@/hooks/useLoanCalculator";
import { Card, CardContent } from "@/components/ui/card";
import { useAnalytics } from "@/hooks/useAnalytics";
import { LainaCta } from "@/components/common/LainaCta";
import { useIsMobile } from "@/hooks/use-mobile";

const HeroSection = () => {
  const [loanAmount, setLoanAmount] = useState(10000);
  const [loanTerm, setLoanTerm] = useState(5);
  const [loanType, setLoanType] = useState("kulutusluotto");
  const { trackEvent, trackNavigationToApplication } = useAnalytics();
  const isMobile = useIsMobile();
  
  const { monthlyPayment, interestRate } = useLoanCalculator(
    loanAmount, 
    loanTerm, 
    loanType
  );

  const applicationUrl = `/kilpailuta?amount=${loanAmount}&term=${loanTerm}&type=${loanType}&source=homepage_calculator`;

  const handleLinkClick = () => {
    trackEvent('hero_calculator_used', {
      loanAmount,
      loanTerm,
      loanType,
      interestRate
    });
    
    trackNavigationToApplication('homepage_calculator', {
      loanType,
      loanAmount,
      loanTerm,
      interestRate
    });
  };

  return (
    <section className="relative bg-gradient-to-r from-blue-600 to-blue-700 text-white w-full overflow-hidden">
      <div className="container py-8 md:py-12 lg:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 lg:gap-8 items-center">
          <div>
            <div className="flex items-center mb-4">
              <h2 className="text-sm md:text-base font-medium text-blue-100 flex items-center">
                <Star className="h-4 w-4 mr-1 fill-yellow-400 text-yellow-400" />
                <span>4,6/5 asiakkaiden arvostelema</span>
              </h2>
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold mb-3 leading-tight">
              Kilpailuta lainat ja säästä rahaa
            </h1>
            <p className="text-base md:text-lg lg:text-xl xl:text-2xl mb-4 md:mb-6 text-blue-100 font-medium">
              Yksi hakemus – useiden pankkien tarjoukset
            </p>
            <p className="text-sm md:text-base lg:text-lg mb-6 md:mb-8 text-blue-100">
              Lainasampo.fi vertailee puolestasi yli 20 rahoitusyhtiön tarjoukset. 
              Saat edullisimman lainan helposti ja turvallisesti.
            </p>
            
            {/* Enhanced trust elements */}
            <div className="flex flex-col gap-2 md:gap-3 mb-4 md:mb-6">
              <div className="flex items-center text-blue-100">
                <Shield className="h-4 w-4 mr-2 text-green-400 flex-shrink-0" />
                <span className="text-xs md:text-sm">100% suomalainen ja tietoturvallinen</span>
              </div>
              <div className="flex items-center text-blue-100">
                <CheckCircle className="h-4 w-4 mr-2 text-green-400 flex-shrink-0" />
                <span className="text-xs md:text-sm font-medium">Hakemus ei sido mihinkään</span>
              </div>
              <div className="flex items-center text-blue-100">
                <span className="text-xs md:text-sm font-medium">✓ Maksuton vertailu • ✓ Yli 25 000 käyttäjää</span>
              </div>
            </div>
            
            <div className="flex flex-col gap-3 md:flex-row md:gap-4">
              <LainaCta 
                to="/kilpailuta"
                text="Kilpailuta lainasi nyt →" 
                variant="primary"
                size={isMobile ? "md" : "lg"}
                className="shadow-md hover:shadow-lg font-semibold"
                dataTestId="hero-main-cta"
              />
              {!isMobile && (
                <Link 
                  to="/lainatuotteet" 
                  className="bg-blue-800 text-white px-4 md:px-6 py-2 md:py-3 rounded-xl font-medium text-sm md:text-base hover:bg-blue-900 transition-colors inline-block text-center shadow-sm hover:shadow-md border border-blue-600"
                >
                  Katso esimerkkilainat
                </Link>
              )}
            </div>
          </div>
          
          <div className="relative w-full mt-6 lg:mt-0">
            <Card className="bg-white p-3 md:p-4 lg:p-6 rounded-2xl text-gray-800 shadow-xl w-full border-0">
              <div className="flex items-center justify-between mb-3 md:mb-4">
                <h2 className="text-base md:text-lg lg:text-xl xl:text-2xl font-bold text-blue-700">Lainalaskuri</h2>
                <div className="bg-blue-100 p-1.5 md:p-2 rounded-full">
                  <Calculator className="text-blue-600" size={isMobile ? 16 : 20} />
                </div>
              </div>
              <CardContent className="p-0 space-y-4 md:space-y-5">
                <div>
                  <p className="mb-2 flex justify-between">
                    <span className="text-base sm:text-lg font-medium">Lainan määrä</span>
                    <span className="font-bold text-base sm:text-lg text-blue-700">{loanAmount.toLocaleString()} €</span>
                  </p>
                  <Slider 
                    min={1000} 
                    max={70000} 
                    step={1000} 
                    value={[loanAmount]} 
                    onValueChange={(value) => setLoanAmount(value[0])}
                    className="my-3"
                  />
                  <div className="flex justify-between text-xs sm:text-sm text-gray-600">
                    <span>1 000 €</span>
                    <span>70 000 €</span>
                  </div>
                </div>
                <div>
                  <p className="mb-2 flex justify-between">
                    <span className="text-base sm:text-lg font-medium">Laina-aika</span>
                    <span className="font-bold text-base sm:text-lg text-blue-700">{loanTerm} vuotta</span>
                  </p>
                  <Slider 
                    min={1} 
                    max={25} 
                    step={1} 
                    value={[loanTerm]} 
                    onValueChange={(value) => setLoanTerm(value[0])}
                    className="my-3"
                  />
                  <div className="flex justify-between text-xs sm:text-sm text-gray-600">
                    <span>1 vuosi</span>
                    <span>25 vuotta</span>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 bg-blue-50 p-4 rounded-xl">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">Kuukausierä:</p>
                    <p className="font-bold text-base sm:text-lg text-blue-700">{monthlyPayment} €</p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">Korko:</p>
                    <p className="font-bold text-base sm:text-lg text-blue-700">{interestRate?.toFixed(2) || '0.00'} %</p>
                  </div>
                </div>
                
                <LainaCta
                  to={applicationUrl}
                  text="Hae lainatarjoukset →"
                  showArrow={false}
                  onClick={handleLinkClick}
                  variant="primary"
                  size="md"
                  className="w-full font-semibold text-base sm:text-lg"
                  dataTestId="hero-calculator-cta"
                />
                
                <p className="text-2xs sm:text-xs text-gray-500 text-center">
                  Esimerkki: 10 000 € laina 5 vuodelle, korko {interestRate?.toFixed(2)}%, 
                  kuukausierä {monthlyPayment} €/kk.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
